import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth-context";
import { Crown } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const from = location.state?.from || "/trips";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back.");
      navigate(from, { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6 pt-24 pb-12">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-3">
          <Crown className="w-5 h-5 text-[#C5A059]" />
          <span className="font-heading text-xl text-white">BLACK<span className="text-[#C5A059]">·</span>SAPPHIRE</span>
        </div>
        <div className="text-[9px] tracking-[0.4em] uppercase text-[#C5A059]/80 text-center mb-10">The Quiet Standard</div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059] text-center">Member Access</div>
        <h1 className="font-heading text-4xl sm:text-5xl text-white text-center mt-4">Welcome back.</h1>
        <p className="text-[#A3A3A3] text-center mt-3 text-sm">Sign in to manage your reservations.</p>

        <form onSubmit={submit} className="mt-12 space-y-8">
          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2">Email</label>
            <input
              data-testid="login-email-input"
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border-b border-white/20 focus:border-[#C5A059] outline-none py-3 text-white text-lg"
              placeholder="you@company.com"
            />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2">Password</label>
            <input
              data-testid="login-password-input"
              required
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border-b border-white/20 focus:border-[#C5A059] outline-none py-3 text-white text-lg"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div data-testid="login-error" className="text-sm text-[#F87171] border-l-2 border-[#F87171] pl-3">{error}</div>
          )}

          <button
            data-testid="login-submit-btn"
            disabled={loading}
            className="w-full bg-white text-[#0A0A0A] hover:bg-[#C5A059] hover:text-white transition-colors duration-300 px-8 py-4 text-xs uppercase tracking-[0.3em] disabled:opacity-50"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>

        <p className="text-center text-sm text-[#A3A3A3] mt-8">
          New to Black Sapphire?{" "}
          <Link to="/register" data-testid="login-to-register-link" className="text-white border-b border-[#C5A059] hover:text-[#C5A059] transition-colors">
            Create an account
          </Link>
        </p>

        <div className="mt-10 pt-8 border-t border-white/10 text-center">
          <p className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-4">In a hurry?</p>
          <Link
            to="/book"
            data-testid="continue-as-guest-link"
            className="inline-block border border-white/20 hover:border-[#C5A059] hover:text-[#C5A059] text-white px-8 py-3 text-xs uppercase tracking-[0.3em] transition-colors"
          >
            Continue as Guest →
          </Link>
          <p className="text-xs text-white/40 mt-3">No account needed. We'll email your receipt.</p>
        </div>
      </div>
    </div>
  );
}
